/**
 * Phase 1 tax settings store — single cache, hydrate, and server sync path.
 * Pure normalize/merge/schedule math stays in tax.ts; this module owns lifecycle.
 */

import {
  getTaxSettingsStorageUserId,
  notifyTaxSettingsChanged,
  setTaxSettingsStorageUserId,
  TAX_SETTINGS_STORAGE_KEY,
} from "./taxEstimatedPayments";

export type TaxStoreSettings = object;

export interface TaxStoreDeps<TSettings extends TaxStoreSettings = TaxStoreSettings> {
  createDefault: () => TSettings;
  loadLocal: () => TSettings;
  saveLocal: (settings: TSettings, opts?: { silent?: boolean }) => void;
  merge: (local: TSettings, server: TSettings) => TSettings;
  localHasPaymentsMissingFromServer: (local: TSettings, server: TSettings) => boolean;
  fetchServer: () => Promise<TSettings | null>;
  pushServer: (settings: TSettings) => Promise<void>;
}

/** Remove legacy + other accounts' tax keys; keep only the bound user's scoped key. */
function purgeForeignTaxSettingsKeys(keepUserId: string | null): void {
  try {
    const keep = keepUserId ? `${TAX_SETTINGS_STORAGE_KEY}:${keepUserId}` : null;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(TAX_SETTINGS_STORAGE_KEY)) continue;
      if (keep && key === keep) continue;
      toRemove.push(key);
    }
    for (const key of toRemove) localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

type Listener = (settings: TaxStoreSettings) => void;

let deps: TaxStoreDeps<TaxStoreSettings> | null = null;
let cache: TaxStoreSettings | null = null;
let revision = 0;
let hydratePromise: Promise<TaxStoreSettings> | null = null;
let serverSaveQueue: Promise<void> = Promise.resolve();
const listeners = new Set<Listener>();

function requireDeps(): TaxStoreDeps<TaxStoreSettings> {
  if (!deps) {
    throw new Error("taxStore: registerTaxStoreDeps() must run before use");
  }
  return deps;
}

export function registerTaxStoreDeps<TSettings extends TaxStoreSettings>(
  next: TaxStoreDeps<TSettings>,
): void {
  deps = next as unknown as TaxStoreDeps<TaxStoreSettings>;
  if (!cache) {
    cache = next.loadLocal();
  }
}

/**
 * Bind tax cache to the signed-in user (or clear on sign-out).
 * Must run before Tax/Analytics hydrate so another account's localStorage is not used.
 */
export function bindTaxStoreUser(userId: string | null): void {
  const next = userId && userId.trim() ? userId.trim() : null;
  const prev = getTaxSettingsStorageUserId();
  setTaxSettingsStorageUserId(next);
  hydratePromise = null;
  revision = 0;
  cache = null;
  // Drop legacy + other accounts' caches. Keep this user's key until hydrate
  // (hydrate resets it when the server tenant is empty).
  purgeForeignTaxSettingsKeys(next);
  if (deps) {
    cache = deps.loadLocal();
  }
  if (prev !== next) {
    notifyTaxSettingsChanged();
  }
}

export function getTaxStoreRevision(): number {
  return revision;
}

/** Sync read — memory cache, else localStorage via deps. */
export function getTaxSettingsFromStore<TSettings extends TaxStoreSettings = TaxStoreSettings>(): TSettings {
  if (cache) return cache as TSettings;
  const loaded = requireDeps().loadLocal();
  cache = loaded;
  return loaded as TSettings;
}

/**
 * Persist to local cache (+ localStorage). Bumps revision.
 * Optionally queues a server PUT (TaxController path).
 */
export function commitTaxSettingsToStore<TSettings extends TaxStoreSettings>(
  settings: TSettings,
  opts?: { silent?: boolean; queueServer?: boolean },
): TSettings {
  const api = requireDeps();
  cache = settings;
  revision += 1;
  api.saveLocal(settings, { silent: true });
  if (!opts?.silent) {
    notifyTaxSettingsChanged();
    for (const listener of listeners) listener(settings);
  }
  if (opts?.queueServer) {
    queueTaxSettingsServerSave(settings);
  }
  return settings;
}

export function queueTaxSettingsServerSave<TSettings extends TaxStoreSettings>(
  settings: TSettings,
): void {
  const api = requireDeps();
  const snapshot = JSON.parse(JSON.stringify(settings)) as TSettings;
  serverSaveQueue = serverSaveQueue
    .catch(() => {})
    .then(async () => {
      await api.pushServer(snapshot);
    })
    .catch(() => {
      // Local copy remains authoritative until the next successful save.
    });
}

/**
 * Single-flight hydrate: merge server into local without wiping newer payments.
 * Safe to call from Analytics and Tax tab concurrently.
 */
export async function ensureTaxStoreReady<TSettings extends TaxStoreSettings = TaxStoreSettings>(): Promise<TSettings> {
  if (!deps) {
    await import("./tax");
  }
  const api = requireDeps();

  if (!hydratePromise) {
    const revisionAtStart = revision;
    const localAtStart = getTaxSettingsFromStore<TSettings>();
    hydratePromise = (async () => {
      try {
        const server = await api.fetchServer();
        if (!server) {
          // Empty tenant is authoritative: wipe browser cache (may still hold a
          // prior leak written under this user id) and never seed the server.
          const fresh = api.createDefault();
          cache = fresh;
          api.saveLocal(fresh, { silent: true });
          notifyTaxSettingsChanged();
          return fresh;
        }
        if (revision !== revisionAtStart) {
          // Local edits won the race — push current cache up.
          queueTaxSettingsServerSave(getTaxSettingsFromStore());
          return getTaxSettingsFromStore();
        }
        const merged = api.merge(localAtStart, server as TSettings);
        cache = merged;
        api.saveLocal(merged, { silent: true });
        if (api.localHasPaymentsMissingFromServer(localAtStart, server as TSettings)) {
          queueTaxSettingsServerSave(merged);
        }
        return merged;
      } catch {
        return getTaxSettingsFromStore();
      }
    })();
  }

  return hydratePromise as Promise<TSettings>;
}

/** Reset single-flight hydrate so the next ensureReady hits the network again. */
export function resetTaxStoreHydrate(): void {
  hydratePromise = null;
}

export function subscribeTaxStore(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
